import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store";
import {
  HeroItem,
  CreateHeroItemRequest,
  UpdateHeroItemRequest,
  ReorderHeroItemsRequest,
} from "../types";
import { fetchHeroItems as fetchHeroItemsApi } from "../api/fetchHeroItems";
import { createHeroItem as createHeroItemApi } from "../api/createHeroItem";
import { updateHeroItem as updateHeroItemApi } from "../api/updateHeroItem";
import { deleteHeroItem as deleteHeroItemApi } from "../api/deleteHeroItem";
import { reorderHeroItems as reorderHeroItemsApi } from "../api/reorderHeroItems";
import { publishHeroItem as publishHeroItemApi } from "../api/publishHeroItem";
import { unpublishHeroItem as unpublishHeroItemApi } from "../api/unpublishHeroItem";

interface HeroPanelAdminState {
  items: HeroItem[];
  loading: boolean;
  error: string | null;
  selectedItem: HeroItem | null;
  publishedCount: number;
}

const initialState: HeroPanelAdminState = {
  items: [],
  loading: false,
  error: null,
  selectedItem: null,
  publishedCount: 0,
};

// Async thunks
export const loadHeroItems = createAsyncThunk(
  "heroPanelAdmin/loadHeroItems",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchHeroItemsApi();
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to load hero items";
      return rejectWithValue(message);
    }
  },
);

export const createHeroItem = createAsyncThunk(
  "heroPanelAdmin/createHeroItem",
  async (data: CreateHeroItemRequest, { rejectWithValue }) => {
    try {
      const heroItem = await createHeroItemApi(data);
      return heroItem;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to create hero item";
      return rejectWithValue(message);
    }
  },
);

export const updateHeroItem = createAsyncThunk(
  "heroPanelAdmin/updateHeroItem",
  async (
    { id, data }: { id: string; data: UpdateHeroItemRequest },
    { rejectWithValue },
  ) => {
    try {
      const heroItem = await updateHeroItemApi(id, data);
      return heroItem;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to update hero item";
      return rejectWithValue(message);
    }
  },
);

export const deleteHeroItem = createAsyncThunk(
  "heroPanelAdmin/deleteHeroItem",
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteHeroItemApi(id);
      return id;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to delete hero item";
      return rejectWithValue(message);
    }
  },
);

export const reorderHeroItems = createAsyncThunk(
  "heroPanelAdmin/reorderHeroItems",
  async (data: ReorderHeroItemsRequest, { rejectWithValue }) => {
    try {
      const response = await reorderHeroItemsApi(data);
      return response.items;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to reorder hero items";
      return rejectWithValue(message);
    }
  },
);

export const publishHeroItem = createAsyncThunk(
  "heroPanelAdmin/publishHeroItem",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await publishHeroItemApi(id);
      return { id, isPublished: response.isPublished };
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to publish hero item";
      return rejectWithValue(message);
    }
  },
);

export const unpublishHeroItem = createAsyncThunk(
  "heroPanelAdmin/unpublishHeroItem",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await unpublishHeroItemApi(id);
      return { id, isPublished: response.isPublished };
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to unpublish hero item";
      return rejectWithValue(message);
    }
  },
);

export const heroPanelAdminSlice = createSlice({
  name: "heroPanelAdmin",
  initialState,
  reducers: {
    setItems: (state, action: PayloadAction<HeroItem[]>) => {
      state.items = action.payload;
    },
    addItem: (state, action: PayloadAction<HeroItem>) => {
      state.items.push(action.payload);
    },
    updateItem: (state, action: PayloadAction<HeroItem>) => {
      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    removeItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
    reorderItems: (
      state,
      action: PayloadAction<Array<{ id: string; displayOrder: number }>>,
    ) => {
      action.payload.forEach(({ id, displayOrder }) => {
        const item = state.items.find((i) => i.id === id);
        if (item) {
          item.displayOrder = displayOrder;
        }
      });
      state.items.sort((a, b) => a.displayOrder - b.displayOrder);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<HeroItem | null>) => {
      state.selectedItem = action.payload;
    },
    updateItemPublishStatus: (
      state,
      action: PayloadAction<{ id: string; isPublished: boolean }>,
    ) => {
      const item = state.items.find((i) => i.id === action.payload.id);
      if (item) {
        item.isPublished = action.payload.isPublished;
      }
      state.publishedCount = state.items.filter((i) => i.isPublished).length;
    },
  },
  extraReducers: (builder) => {
    builder
      // loadHeroItems
      .addCase(loadHeroItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadHeroItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.publishedCount = action.payload.publishedCount;
      })
      .addCase(loadHeroItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // createHeroItem
      .addCase(createHeroItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createHeroItem.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        if (action.payload.isPublished) {
          state.publishedCount++;
        }
      })
      .addCase(createHeroItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateHeroItem
      .addCase(updateHeroItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateHeroItem.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(
          (item) => item.id === action.payload.id,
        );
        if (index !== -1) {
          const wasPublished = state.items[index].isPublished;
          state.items[index] = action.payload;
          // Update published count if status changed
          if (wasPublished && !action.payload.isPublished) {
            state.publishedCount--;
          } else if (!wasPublished && action.payload.isPublished) {
            state.publishedCount++;
          }
        }
      })
      .addCase(updateHeroItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deleteHeroItem
      .addCase(deleteHeroItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteHeroItem.fulfilled, (state, action) => {
        state.loading = false;
        const deletedItem = state.items.find(
          (item) => item.id === action.payload,
        );
        if (deletedItem?.isPublished) {
          state.publishedCount--;
        }
        state.items = state.items.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteHeroItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // reorderHeroItems
      .addCase(reorderHeroItems.fulfilled, (state, action) => {
        action.payload.forEach(({ id, displayOrder }) => {
          const item = state.items.find((i) => i.id === id);
          if (item) {
            item.displayOrder = displayOrder;
          }
        });
        state.items.sort((a, b) => a.displayOrder - b.displayOrder);
      })
      // publishHeroItem
      .addCase(publishHeroItem.fulfilled, (state, action) => {
        const item = state.items.find((i) => i.id === action.payload.id);
        if (item) {
          item.isPublished = action.payload.isPublished;
          state.publishedCount++;
        }
      })
      .addCase(publishHeroItem.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // unpublishHeroItem
      .addCase(unpublishHeroItem.fulfilled, (state, action) => {
        const item = state.items.find((i) => i.id === action.payload.id);
        if (item) {
          item.isPublished = action.payload.isPublished;
          state.publishedCount--;
        }
      })
      .addCase(unpublishHeroItem.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

// Actions
export const {
  setItems,
  addItem,
  updateItem,
  removeItem,
  reorderItems,
  setLoading,
  setError,
  setSelectedItem,
  updateItemPublishStatus,
} = heroPanelAdminSlice.actions;

// Selectors
export const selectHeroItems = (state: RootState) => state.heroPanelAdmin.items;
export const selectHeroItemsLoading = (state: RootState) =>
  state.heroPanelAdmin.loading;
export const selectHeroItemsError = (state: RootState) =>
  state.heroPanelAdmin.error;
export const selectSelectedHeroItem = (state: RootState) =>
  state.heroPanelAdmin.selectedItem;
export const selectPublishedCount = (state: RootState) =>
  state.heroPanelAdmin.publishedCount;

// Reducer
export const heroPanelAdminReducer = heroPanelAdminSlice.reducer;
export default heroPanelAdminReducer;
